// /backups — Backup Management (PAGES.md §3.74)
//
// View backup history, trigger manual backups, download. OWNER+ (50).
// Auto-polls every 5s when QUEUED/IN_PROGRESS. FR-BACK-001 to FR-BACK-010

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  PageContainer,
  Button,
  Badge,
  Alert,
  ConfirmDialog,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  StatCard,
  DataTable,
  Pagination,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { backupService } from '@/services/backup.service';
import type { Backup, BackupStatus } from '@/services/backup.service';
import { ApiError } from '@/lib/api-client';
import { GLASS, cn } from '@/constants/design-tokens';
import { DEFAULT_BACKUP_HOUR_UTC, formatNextScheduledBackup } from './next-scheduled-backup';
import { useStoreTimezone } from '@/lib/store-clock';
import { blockedDownloadNotice } from './download-link';
import { triggerBackupErrorMessage } from './trigger-backup-error';
import { formatBytes, formatTimestamp } from './backup-format';

// ─── Constants ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;
const POLL_INTERVAL_MS = 5000;

type BadgeVariant = 'success' | 'info' | 'danger' | 'draft' | 'warning' | 'pending';

const STATUS_BADGE: Record<BackupStatus, { label: string; variant: BadgeVariant }> = {
  COMPLETED: { label: 'Completed', variant: 'success' },
  IN_PROGRESS: { label: 'In Progress', variant: 'info' },
  FAILED: { label: 'Failed', variant: 'danger' },
  QUEUED: { label: 'Queued', variant: 'draft' },
};

// ─── Icons ─────────────────────────────────────────────────────────────────

function DatabaseIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </svg>
  );
}

function DatabaseEmptyIcon() {
  return (
    <svg className="h-14 w-14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
    </svg>
  );
}

function ClockIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function PackageIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
    </svg>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

function BackupManagementContent() {
  const { toast } = useToast();

  const [backups, setBackups] = useState<Backup[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  // Tenant-wide, from the response meta — not reduced from the current page.
  const [totalSizeBytes, setTotalSizeBytes] = useState<number | null>(null);
  const [lastCompletedAt, setLastCompletedAt] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isTriggering, setIsTriggering] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => { document.title = 'Backups — Orio360 Admin'; }, []);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await backupService.list({ page });
      setBackups(result.data);
      setTotalItems(result.meta.total);
      setTotalSizeBytes(result.meta.total_size_bytes ?? null);
      setLastCompletedAt(result.meta.last_completed_at ?? null);
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load backups', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [page, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-poll when any backup is pending
  const hasPending = backups.some((b) => b.status === 'QUEUED' || b.status === 'IN_PROGRESS');
  useEffect(() => {
    if (hasPending) {
      pollRef.current = setInterval(() => { fetchData(); }, POLL_INTERVAL_MS);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [hasPending, fetchData]);

  const handleTrigger = useCallback(async () => {
    setIsTriggering(true);
    try {
      await backupService.trigger();
      toast({ title: 'Backup triggered — it will appear in the list shortly', variant: 'success' });
      fetchData();
    } catch (err) {
      toast({ title: triggerBackupErrorMessage(err), variant: 'error' });
    } finally {
      setIsTriggering(false);
    }
  }, [fetchData, toast]);

  const handleDownload = useCallback(async (id: string) => {
    try {
      const { download_url } = await backupService.getDownloadUrl(id);
      // Without S3 the API returns a file:// path on its own disk, which the
      // browser refuses to open — say where the backup is instead.
      const blocked = blockedDownloadNotice(download_url);
      if (blocked) {
        toast({ title: blocked.title, description: blocked.description, variant: 'warning', duration: 10000 });
        return;
      }
      window.open(download_url, '_blank');
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to get download link', variant: 'error' });
    }
  }, [toast]);

  // Computed stats. The banner keys off the NEWEST backup only (the list is
  // created_at DESC) — flagging any failed row on the page kept "Last backup
  // failed" on screen even after a later backup had succeeded.
  const failedBackup = page === 1 && backups[0]?.status === 'FAILED' ? backups[0] : undefined;
  // On the business's clock (T28), not a fixed IST one.
  const timeZone = useStoreTimezone();
  const nextScheduled = useMemo(
    () => formatNextScheduledBackup(DEFAULT_BACKUP_HOUR_UTC, new Date(), timeZone),
    [timeZone],
  );

  const columns: ColumnDef<Backup>[] = useMemo(() => [
    {
      key: 'created_at', header: 'Date',
      render: (_v: unknown, row: Backup) => (
        <span className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400">{formatTimestamp(row.completed_at ?? row.started_at ?? row.created_at)}</span>
      ),
    },
    {
      key: 'type', header: 'Type',
      render: (_v: unknown, row: Backup) => (
        <Badge variant={row.type === 'SCHEDULED' ? 'draft' : 'info'} size="sm">{row.type === 'SCHEDULED' ? 'Auto' : 'Manual'}</Badge>
      ),
    },
    {
      key: 'file_size_bytes', header: 'Size', numeric: true,
      render: (_v: unknown, row: Backup) => (
        <span className="text-sm font-mono tabular-nums text-neutral-700 dark:text-neutral-300">{formatBytes(row.file_size_bytes)}</span>
      ),
    },
    {
      key: 'status', header: 'Status',
      render: (_v: unknown, row: Backup) => {
        const s = STATUS_BADGE[row.status];
        return (
          <Badge variant={s.variant} size="sm" className={row.status === 'IN_PROGRESS' ? 'animate-pulse' : ''}>
            {s.label}
          </Badge>
        );
      },
    },
    {
      key: 'id', header: '',
      render: (_v: unknown, row: Backup) => (
        row.status === 'COMPLETED' ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleDownload(row.id); }}
            aria-label={`Download backup from ${formatTimestamp(row.completed_at ?? row.created_at)}`}
            className="text-xs font-medium text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-200 transition-colors duration-200 flex items-center gap-1"
          >
            <DownloadIcon /> Download
          </button>
        ) : row.status === 'FAILED' ? (
          <span className="text-xs text-danger-500 dark:text-danger-400" title={row.error_message ?? undefined}>
            {row.error_message
              ? row.error_message.length > 40 ? `${row.error_message.slice(0, 40)}…` : row.error_message
              : 'Error'}
          </span>
        ) : null
      ),
    },
  ], [handleDownload]);

  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  return (
    <PageContainer
      title="Backups"
      subtitle="Data protection and backup management"
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Backups' }]}
      actions={
        <Button
          variant="primary"
          size="sm"
          onClick={() => setConfirmOpen(true)}
          disabled={isTriggering || hasPending}
          loading={isTriggering}
        >
          {hasPending ? 'Backup in Progress…' : 'Trigger Backup Now'}
        </Button>
      }
    >
      <div className="animate-fade-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-emerald-700 rounded-2xl p-6 text-white shadow-[0_20px_60px_-20px_rgba(13,148,136,0.5)] mb-6 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/15 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
          <div className="relative flex items-center gap-3">
            <DatabaseIcon className="h-7 w-7 text-teal-200" />
            <div>
              <h1 className="text-2xl font-bold text-white">Backup Management</h1>
              <p className="text-teal-100 mt-1">Scheduled and on-demand data backups with S3 storage</p>
            </div>
          </div>
        </div>

        {/* Failed backup alert */}
        {failedBackup && (
          <div className="mb-6 animate-slide-up">
            <Alert variant="danger" title="Last backup failed" description={failedBackup.error_message ?? 'The most recent backup encountered an error. Please retry or contact support.'} />
          </div>
        )}

        {/* Stat Cards */}
        {isLoading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[1, 2, 3, 4].map((i) => <StatCard key={i} title="" value="" loading />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="animate-slide-up"><StatCard title="Last Backup" value={formatTimestamp(lastCompletedAt)} icon={<ClockIcon className="h-4 w-4" />} /></div>
            <div className="animate-slide-up" style={{ animationDelay: '50ms' }}><StatCard title="Next Scheduled" value={nextScheduled} icon={<ClockIcon className="h-4 w-4" />} /></div>
            <div className="animate-slide-up" style={{ animationDelay: '100ms' }}><StatCard title="Total Backups" value={totalItems.toLocaleString('en-IN')} icon={<PackageIcon className="h-4 w-4" />} /></div>
            <div className="animate-slide-up" style={{ animationDelay: '150ms' }}><StatCard title="Storage Used" value={formatBytes(totalSizeBytes)} icon={<DatabaseIcon className="h-4 w-4" />} /></div>
          </div>
        )}

        {/* Table */}
        {isLoading ? (
          <div className={cn(GLASS, 'p-6 space-y-3')}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex gap-4">
                <Skeleton variant="text" width="20%" height={16} />
                <Skeleton variant="rect" width={60} height={22} />
                <Skeleton variant="text" width="10%" height={16} />
                <Skeleton variant="rect" width={70} height={22} />
                <Skeleton variant="text" width="10%" height={16} />
              </div>
            ))}
          </div>
        ) : backups.length === 0 ? (
          <EmptyState variant="page" icon={<DatabaseEmptyIcon />} title="No backups yet" description="Create your first backup to protect your business data" action={{ label: 'Create First Backup', onClick: handleTrigger }} />
        ) : (
          <>
            <div className={cn(GLASS, 'overflow-hidden mb-6')}>
              <div className="px-5 py-4">
                <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                  Backup History ({totalItems.toLocaleString('en-IN')})
                </h3>
              </div>
              <DataTable<Backup> columns={columns} data={backups} />
            </div>
            {totalPages > 1 && <div className="mb-6"><Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems} pageSize={PAGE_SIZE} onPageChange={setPage} /></div>}
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Trigger Backup"
        description="Start a manual backup now? This may take a few minutes."
        confirmLabel="Trigger Backup"
        variant="warning"
        onConfirm={() => {
          setConfirmOpen(false);
          void handleTrigger();
        }}
        onCancel={() => setConfirmOpen(false)}
        loading={isTriggering}
      />
    </PageContainer>
  );
}

export function BackupManagementPage() {
  return (
    <ErrorBoundary>
      <BackupManagementContent />
    </ErrorBoundary>
  );
}
